import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ElementType } from "ng-whiteboard";
import { createSignalRHub, findHub } from "ngrx-signalr-core";
import { map, take, tap, withLatestFrom } from "rxjs";
import { CanvasData } from "src/app/components/whiteboard/whiteboard.component";
import { Constants } from "src/app/constants";
import { AppHelper } from "src/app/helpers/app.helper";
import { DisclaimerHelper } from "src/app/helpers/disclaimer.helper";
import { ManHelper } from "src/app/helpers/man.helper";
import { WhiteboardHelper } from "src/app/helpers/whiteboard.helper";
import { AppModesEnum } from "src/app/models/WebServer/appModesEnum";
import { CreateEvent } from "src/app/models/WebServer/createEvent";
import { EventTypeEnum } from "src/app/models/WebServer/eventTypeEnum";
import { MarkingReadObject } from "src/app/models/WebServer/markingReadObject";
import { Message } from "src/app/models/WebServer/message";
import { OPERATOR_ROLES, PartecipantRoles } from "src/app/models/WebServer/partecipantRoles";
import { PartecipantStatus, partToStr } from "src/app/models/WebServer/partecipantStatus";
import { initialSessionConfig } from "src/app/models/WebServer/sessionConfig";
import { CommandEvent, SharedWhiteboard } from "src/app/models/commandEvent";
import { StatusChangeEvent } from "src/app/models/statusChangeEvent";
import { AppConfigService } from "src/app/services/app.config.service";
import { CheckerService } from "src/app/services/checker.service";
import { EventService } from "src/app/services/event.service";
import { FELoggerService, LogSource } from "src/app/services/feLogger.service";
import { I18Service } from "src/app/services/i18.service";
import { MediaStremService } from "src/app/services/media-stream.service";
import { NotificationService } from "src/app/services/notification.service";
import { SessionService } from "src/app/services/session.service";
import { SipService } from "src/app/services/sip.service";
import { TextTranslationService } from "src/app/services/textTranslation.service";
import { WhiteboardService } from "src/app/services/whiteboard.service";
import Swal from "sweetalert2";
import * as appActions from "../actions/app.actions";
import * as sessionActions from "../actions/session.actions";
import * as sessionStatusActions from "../actions/sessionStatus.actions";

import { selectAppState } from "../selectors/app.selectors";
import { myPartecipantStatus, selectPartecipantsStatus, selectSessionStatusState } from "../selectors/sessionStatus.selectors";
import { AppState } from "../states/app.state";

// const RETRY_COUNT = 5;
const RETRY_DELAY = 6000;
@Injectable()
export class AppEffects {

  constructor(
    private readonly eventService: EventService,
    private readonly checkerService: CheckerService,
    private readonly sessionService: SessionService,
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly sip: SipService,
    private readonly media: MediaStremService,
    private readonly config: AppConfigService,
    private readonly textTranslationService: TextTranslationService,
    private readonly whiteboardService: WhiteboardService,
    private readonly i18Service: I18Service,
  ) {
    console.debug(`AppEffects constructor`);
  }

  configLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.configLoaded),
    map((_) => sessionActions.sessionCheck())
  )
  );
  

  onSessionLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(sessionActions.sessionLoaded),
    // withLatestFrom(this.store.select(selectSignalrHub)),
    map((_) => {
      // [PERF-LOAD-09] SignalR Hub Created
      const perfNow = performance.now();
      const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
      console.log(`[PERF-LOAD-09] SignalR Hub Created | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

      return createSignalRHub({...this.config.signalRHub, automaticReconnect: {
        nextRetryDelayInMilliseconds: (context) => {
          if(context.previousRetryCount > Constants.MAX_ONREJECT_RETRY -1 ) return null;
          console.log(`********* previousRetryCount ${context.previousRetryCount}`);
          this.store.dispatch(appActions.loadingMessage(
            {message: I18Service.get('SNACKBAR.RECONNECTIONATTEMPT', {current:context.previousRetryCount+1, max: Constants.MAX_ONREJECT_RETRY})}
          ));
          this.whiteboardService.close(); //chiude la whiteboard se aperta in caso di errore di connessione
          return RETRY_DELAY;
        }
      }
    })})


  ));

  wssSipInit$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.wssSipInit),
    tap(_ => { 
      this.sip.init(); 
      var firstLoad= true;

      const loadChatHistory = () => {

        this.eventService.getChatHistory(this.config.partecipantId).subscribe({
          next: (messages) => {
            // [PERF-LOAD-17] Chat History Loaded
            const perfNow = performance.now();
            const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
            console.log(`[PERF-LOAD-17] Chat History Loaded - messages: ${messages.length} | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

            console.log(`chat history loaded`);
            this.store.dispatch(sessionActions.chatHistoryLoaded({ messages}));

            if (firstLoad) {
              firstLoad = false;
              window.setTimeout(()=>{
                console.log(`load again chat history`);
                loadChatHistory();
              }, AppConfigService.CHATHISTORY_RELOAD * 1000) ;
            }
          },
          error: (error: any) => {
            // return of(sessionActions.sessionFailure({ error }));
            FELoggerService.error(`getChatHistory error`, LogSource.Default, error)
            NotificationService.showError(error.statusText || error.message || error + '');
          }
          
        })
      }
      loadChatHistory();
    })

  ), { dispatch: false });

  wssSipRegistered$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.wssSipRegistered),
    map((b) => {
      if (b.is) {
        // [PERF-LOAD-16] Ready to Start Dispatched
        const perfNow = performance.now();
        const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
        console.log(`[PERF-LOAD-16] Ready to Start Dispatched | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

        this.store.dispatch(appActions.loadingMessage({message: ''}));
        NotificationService.showSuccess(I18Service.get('SNACKBAR.CONNECTED'));
        this.store.dispatch(appActions.readyToStart());

      } else {
        // this.store.dispatch(appActions.showErrorPage({ message: I18Service.get('POPUP.SESSIONEXPIREDTITLE') }))
        // NotificationService.swalWarn(, I18Service.get('POPUP.CLOSE'))
        NotificationService.showError(I18Service.get('SNACKBAR.DISCONNECTED'));
      }
      return sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.IsRegistered, val: b.is })
    })
  )
  );


  showErrorPage$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.showErrorPage),
    // withLatestFrom(this.store.select(selectSignalrHub)),
    tap((x) => {
      // chiude tutto quando mostra pagina di errore
      FELoggerService.info(`showErrorPage$ -> ${x.message}`)
      const hub = findHub(this.config.signalRHub);
      if (hub && hub.connectionId) {
        hub.stop(); // chiudere.. significa non poter inviare gli ultimi stati
      }
      this.checkerService.stop();
      this.sip.disconnect();
      this.whiteboardService.close();
    })

  ), { dispatch: false });


  cmd_event$ = createEffect(() => this.actions$.pipe(
    ofType(sessionStatusActions.cmd_event),
    withLatestFrom(this.store.select(selectAppState), this.store.select(selectSessionStatusState)),
    tap(([{ evt, val }, state, sessionState]) => {
      console.log(`cmd_event$ - ${evt} => ${val}`);
      switch (evt) {
        case CommandEvent.AcceptedBrowser:
          if (val) { // operatore forza l'ok
            this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.BrowserControlResult, val: { success: true, message: '' } }));
          } else { // operatore rifiuta il browser non valido
            this.store.dispatch(appActions.showErrorPage({ message: I18Service.get('ERRORPAGE.UNSUPPORTEDDEVICE'), imagePath: 'assets/images/unsupported-device.png' }))
          }
          break;
        case CommandEvent.ChangeMode:
          NotificationService.closeMultimediaDialog();
          // Se il comando è "torna a Map", calcola lo stato iniziale (potrebbe essere landing se no operatori)
          if (val === AppModesEnum.Map) {
            const anyOpOnline = this.config.operatorsStatus.length > 0;
            const myStatus = this.config.myPartecipantStatus;
            const sessionConfig = this.config.sessionConfig || initialSessionConfig;
            const modes = AppHelper.getHome(sessionConfig, myStatus?.partecipantRole || PartecipantRoles.SecondaryClient, anyOpOnline);
            console.log(`[APPMODES] cmd_event ChangeMode(Map) → goHome, anyOpOnline: ${anyOpOnline}, modes: [${modes}]`);
            this.store.dispatch(appActions.setAppModes({ modes }));
          } else {
            this.store.dispatch(appActions.appModeChange({ mode: val }));
          }
          break;

        case CommandEvent.ToggleMicrophone:
          this.store.dispatch(appActions.toggleMicrophone({}));
          break;
        case CommandEvent.ToggleVideo:
          
          if(this.config.isCaller && !state.videoEnabled && !state.appModes.includes(AppModesEnum.Video)) { //il toggle lo abiliterà.. forza ad andare in video
              //const appModes = [...state.appModes];
              this.store.dispatch(appActions.appModeChange({mode: AppModesEnum.Video}));
          } else {
            this.store.dispatch(appActions.toggleVideo({}));
          }
          break;
        case CommandEvent.EnableSharedWhiteboard:
          // this.store.dispatch(appActions.rtWhiteboardEnabled({ data: val }));
          if(val.enabled) {
            // if( !state.appModes.includes(AppModesEnum.Whiteboard)) {
            //   this.store.dispatch(appActions.appModeChange({ mode: AppModesEnum.Whiteboard }));
            // }
            // const mode = val.type == 'video' ? AppModesEnum.Video : AppModesEnum.Photo;
            // this.store.dispatch(appActions.appModeChange({ mode}));
            this.config.whiteboardBackup();
            if (this.whiteboardService.isOpen) {
              this.whiteboardService.close(); //che succede?? scattano??
            }

            this.sessionService.getWhiteboardImage(this.config.sessionId!).subscribe({
              next: (image) => {
                // this.store.dispatch(appActions.rtWhiteboardImage({ canvasData: image, who: val.who }));
                console.log(`getWhiteboardImage`, image, val.who);
                this.config.setWhiteboardSharedCanvas(image, val.who);
                image = image || this.config.getWhiteboardBackupCanvas(); //  whiteboard.canvasData; // se non c'è l'immagine, riapre l'ultima immagine
                this.openWhiteboardImage(image);

              },
              error: (error) => {
                FELoggerService.warn(`getWhiteboardImage error`, error);
              }
            });
          } else {
            if(this.config.whiteboardSharingEnabled || this.config.whiteboardIsReceivingShare ) {
              this.config.whiteboardReset();
              this.whiteboardService.close(); //che succede?? scattano??
              setTimeout(() => {
                const image = this.config.getWhiteboardBackupCanvas();//  whiteboard.canvasData; // se non c'è l'immagine, riapre l'ultima immagine
                this.openWhiteboardImage(image);
              }, 0);
            }

          }
          this.store.dispatch(sessionStatusActions.evt_changeStatus({ 
            evt: StatusChangeEvent.WhiteboardStatus, val: { enabled: val.enabled ? 'full' : 'none' }  // utile per sapere chi ha aperto la shared whiteboard
          }))
          break;
        case CommandEvent.WhiteboardClear:
          if (this.whiteboardService.isOpen) {
            const ngWhiteboardService = this.whiteboardService.getWhiteboardComponent()?.ngWhiteboardService;
            // if(ngWhiteboardService) {
            //   ngWhiteboardService.
            // }
            
            // const image = this.config.whiteboard.canvasData;
            // this.config.whiteboardReset();
            // this.whiteboardService.close();
            // if(image)
            //   this.openWhiteboardImage(image);
          }
          break;
        case CommandEvent.WhiteboardElements:
          const elements = WhiteboardHelper.unzipWhiteboardElements(val);
          this.config.setWhiteboardElements(elements);
          this.whiteboardService.getWhiteboardComponent()?.ReplaceElements(elements)
          break;

        case CommandEvent.WhiteboardRelativeTransform:
          const bounds = val;
          this.config.setWhiteboardRelativeTransform(val); //viene eseguito dal servizio whiteboard
          if (this.whiteboardService.isOpen) {
            this.whiteboardService.getWhiteboardComponent()?.ngWhiteboardService.applyRelativeTransform(val);
          }
          // this.store.dispatch(appActions.rtWhiteboardViewChange({bounds: val}));
          break;


        case CommandEvent.WhiteboardTakeControl:
          const who = val;
          const info = this.config.partecipantIds.find(z => z.partecipantId == who)
          NotificationService.popupTimerQuestion({msg:I18Service.get('Whiteboard.TakeControl.msg', {who: I18Service.roleAndAlias(info)})}).then((x) => { 
            // console.log('PPPPPPPPPPPPPPPPPPPPP =>', x)
            const really = x.isConfirmed || x.dismiss == Swal.DismissReason.timer;
            // this.store.dispatch(appActions.rtWhiteboardControlTaken({who: val, really}));
            if(really){
              this.config.setWhiteboardControlLoose(who);

              // Aggiorna le options della whiteboard per disabilitare il drawing
              const whiteboardComp = this.whiteboardService.getWhiteboardComponent();
              if (whiteboardComp) {
                whiteboardComp.updateOptions({drawingEnabled: false});
                // Deseleziona tutti gli elementi per non lasciare tracce
                whiteboardComp.ngWhiteboardService.clearSelection();
              }
            }
            
            console.log(`[WB-SAVE] TakeControl SENDING response: really=${really}, who=${who}, hasUnsavedChanges=${this.config.whiteboardHasUnsavedChanges}`);
            this.store.dispatch(sessionStatusActions.cmd_dispatch({
                cmd: CommandEvent.WhiteboardTakeControlResponse,
                val: {really, who, config: this.config.whiteboardHasUnsavedChanges },
                toIds: appActions.SEND_TO_ALL
            }));
          });

          break;
        case CommandEvent.WhiteboardTakeControlResponse:
            // val.config contiene lo stato unsavedChanges dell'owner precedente
            console.log(`[WB-SAVE] TakeControlResponse RECEIVED: really=${val.really}, who=${val.who}, inheritedUnsaved=${val.config}, myId=${this.config.partecipantId}`);
            this.whiteboardService.getWhiteboardComponent()?.TakeControlResponse(val.really, val.who, val.config);
          break;
        case CommandEvent.SwitchCamera:
          this.store.dispatch(appActions.switchCamera({forSnapshot: false}));
          break;          
        case CommandEvent.ToggleRemoteCommand:
          this.store.dispatch(appActions.toggleRemoteCommand({ enabled: val }));
          break;

        case CommandEvent.Session_Terminate:
          this.sip.endAllCalls();
          this.store.dispatch(appActions.sessionExpired());
          const errorMessage = I18Service.get('POPUP.SESSIONEXPIREDBODY');
          this.store.dispatch(appActions.showErrorPage({ message: errorMessage, showReload: false }))
          //     this.config.isCaller && this.eventEmitter.riseError(new ErrorInfo('', message, -1, undefined, false));
          break;

        case CommandEvent.GeoPermissionInstructions:

          ManHelper.getGeoMessageAccordingToBrowser(this.config.language)
            .finally(()=>{
              this.checkerService.newGeoLocAttempt();
            }); 
          
          break;
        case CommandEvent.MediaPermissionInstructions:
          ManHelper.getMediaMessageAccordingToBrowser(this.config.language);
          break;
        case CommandEvent.ForceRefresh:
          this.sip.endAllCalls();
          NotificationService.timedToastWarn(I18Service.get('LABELS.OPERATORASKREFRESH'), 'OK', 3000, 'top').then(() => {
            window.location.reload();
          });
          break;
        case CommandEvent.MediaMessageSent:
          const message = val as Message;
          this.store.dispatch(appActions.recvMediaMessage({ message }));

					// non viene più visualizzato popup di scaricamento file!
          // if (message.type == MediaTypesEnum.Document && !this.config.showChatPreview(message) ) {
          //   const sender = sessionState.sessionStatus.partecipants.find(z => z.partecipantId == message.from);
            
          //   const who = sender ? whoIs(sender): I18Service.get(PartecipantRoles.PrimaryClient);
          //   this.config.popupFilename = message.fileName?.trim() || ''
          //   this.config.popupLabelNewDocument = I18Service.get('LABELS.NEWDOCUMENT').replace('__WHO__', who);
          //   this.config.popupUrl=message.content;
          //   NotificationService.showDocumentToDownload(message.content, message.fileName?.trim() || '', who);
          // }


          break;
        case CommandEvent.MarkAsDelivered:
          this.store.dispatch(appActions.markMessageDelivered({ message: val }))
          break;
        case CommandEvent.MarkAllAsRead: //notifica che gli altri hanno letto: doppia spunta (in futuro si potrebbe controllare chi...)
          if (state.messages.some(m => m.me && !m.messageInfo?.readAt)) {
            this.eventService.markAllAsRead(state.partecipantId).subscribe({
              next: () => { this.store.dispatch(appActions.markMyMessageAsRead({ message: val })); }
            });

          }

          break;

        case CommandEvent.EnableDebug:
          const enable = val as boolean;
          this.config.logDebug(enable);
          break;
        case CommandEvent.ReloadSession:
          // Richiesta di reload session ricevuta via comando SignalR
          this.store.dispatch(sessionActions.sessionReloadFromServer());
          break;
        case CommandEvent.ChangeLanguage:
          // Cambio lingua richiesto dal backend (CAD)
          const targetParticipantId = val?.targetParticipantId;
          const targetRoles = val?.targetRoles;
          const newLanguage = val?.language;

          // Verifica se il comando è per questo partecipante
          let shouldApply = false;

          if (targetParticipantId) {
            // Target specifico per ID
            shouldApply = targetParticipantId === this.config.partecipantId;
          } else if (targetRoles && targetRoles.length > 0) {
            // Target per ruolo
            shouldApply = targetRoles.includes(this.config.AS?.role);
          } else {
            // Nessun target specificato = tutti
            shouldApply = true;
          }

          if (shouldApply && newLanguage && Constants.ALLOWED_LANGUAGES.includes(newLanguage)) {
            console.log(`ChangeLanguage: applying language ${newLanguage}`);
            this.i18Service.saveLanguage(newLanguage).then(() => {;
              NotificationService.showSuccess(I18Service.get('SNACKBAR.SYSTEM_LANGUAGE_CHANGED'));
            });
          } else if (shouldApply) {
            FELoggerService.warn(`ChangeLanguage: invalid language ${newLanguage}`);
          } else {
            console.log(`ChangeLanguage: not for me (target: ${targetParticipantId}, roles: ${targetRoles})`);
          }
          break;
        case CommandEvent.SendMessage: //message arrived
          const msg: Message = val as Message;
          this.store.dispatch(appActions.recvTextMessage({ body: msg.content, contentLanguage: msg.contentLanguage, trInfo: msg.translationInfo, from: msg.from, rtpCallId: msg.rtpCallId }))
          break;

        case CommandEvent.VideoZoomTo:
          this.store.dispatch(appActions.zoomTo({id: val as string}));
          break;
        
        case CommandEvent.FitVideoTo:
          this.store.dispatch(appActions.fitVideoTo({id: val.id, val: val.value}));
          break;

        case CommandEvent.OperatorZoomId:
          this.store.dispatch(appActions.operatorZoomId({id: val}));
          break;

        case CommandEvent.CloseCurrentCall:
          this.closeCallsGoToHome();
          break;       
      }
    })

  ), { dispatch: false });

  send_text_message$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.sendTextMessage),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ body, id, trInfo, toIds }, state]) => {

      const toPartecipants = toIds.includes(appActions.SEND_TO_ALL[0])
        ? state.session.parameters.partecipantIds.filter(z => z.partecipantId != state.partecipantId).map(z => z.partecipantId)
        : toIds

      // toPartecipants.forEach((p, idx) => {
      //   this.sip.sendMessage(body, p, idx === 0);
      // });
      const msg : Message = {
        content : body,
        contentLanguage: I18Service.getNavigatorLanguage(),
        from: state.partecipantId,
        idx: 0,
        timestamp: new Date(),
        translationInfo: trInfo,
        rtpCallId: id
      }

      this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.SendMessage, val: msg, toIds}));
    })
  ), { dispatch: false });

  updateMessage$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.updateMessage),
    tap(({message}) => {
      const event : CreateEvent = {
        data: {
          partecipantId : message.from,
          message : message.content,
          messageInfo: message.messageInfo,
          translationInfo: message.translationInfo,
        },
        eventType: EventTypeEnum.ChatMessage,
        rtpCallId: message.rtpCallId       
      }
      this.eventService.updateEvent(event).subscribe({
        next: (eventId : number) => {
          console.log(`updated message with eventId: ${eventId}`);
        },
        error: (e: any) => { 
          if(e.status != 404)
          NotificationService.showError(e.statusText); 
        }
      });
    })
  ), { dispatch: false })

  send_media_message$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.sendMediaMessage),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ content, mediaType, fileName, id, toIds }, state]) => {

      // const toPartecipants = toIds.includes(appActions.CHAT_TO_ALL)
      //   ? state.session.parameters.partecipantIds.filter(z => z.partecipantId != state.partecipantId).map(z => z.partecipantId)
      //   : toIds

      // toPartecipants.forEach((p, idx) => {
      //   this.sip.sendMessage(body, p, idx === 0);
      // });
      const m: Message = {
        idx: 0,
        from: state.partecipantId,
        content,
        timestamp: new Date(),
        type: mediaType,
        fileName,
        rtpCallId: id
      }
      this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.MediaMessageSent, val: m, toIds }))

      // invio anche a "se stesso" su chat a meno che non sia caller e ha inviato un immagine bug #62447
      // if ( !(this.config.isCaller && mediaType == MediaTypesEnum.Image)) { //#92039 ora anche le immagini inviate da cittadino vanno in chat
        this.store.dispatch(appActions.recvMediaMessage({ message: m }));
      // }

    })
  ), { dispatch: false });

  partecipantAdded = createEffect(() => this.actions$.pipe(
    ofType(sessionStatusActions.partecipantAdded),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ partecipantStatus, sessionStatus }, state]) => {
    // tap(({ partecipantStatus }) => {
      
      if (partecipantStatus.partecipantId === this.config.partecipantId) { // se sono io attivo il guard 

        // GUARD: Verifica il mio appModes dopo 5 secondi
        this.handleStaleAppModes(state);

        return; // Non eseguire il codice per altri partecipanti
      }


      if (partecipantStatus.partecipantId != this.config.partecipantId) { // se non sono io
        const who = whoIs(partecipantStatus);
        
        NotificationService.showSuccess(`${who} ${I18Service.get('who.Connected')}`);

        //in caso di riconnessione.. eliminare (chiudere) eventuali chiamate attive
        console.log(`partecipantAdded - close any call with ${partToStr(partecipantStatus)}`)
        this.sip.endCall(partecipantStatus.partecipantId);

        if (this.config.isReceiver) { // se sono un operatore, setta per gli altri il mio zoomid
          const id = this.config.AS?.operatorZoomId;
          this.store.dispatch(sessionStatusActions.cmd_dispatch({cmd: CommandEvent.OperatorZoomId,val:id, toIds: [partecipantStatus.partecipantId]}))
        }

        if (OPERATOR_ROLES.includes(partecipantStatus.partecipantRole) && this.config.iamOnCall) { //se è entrato un operatore e sono in chiamata

          // Chiama SOLO se l'operatore è già registrato su SIP.
          // Se non è ancora registrato, l'effect operatorSipRegistered reagirà
          // al partecipantStatusChange con isRegistered:true e triggererà handleCalls.
          if (partecipantStatus.isRegistered) {
            this.sip.handleCalls()
          } else {
            console.log(`partecipantAdded - operator ${partecipantStatus.partecipantId} not yet SIP registered, deferring call to registration event`);
          }

        } else
        // se sono cittadino e si aggiunge un primary.. e sono senza localizzazion, fermo in attesa
        if (this.config.isCaller && OPERATOR_ROLES.includes(partecipantStatus.partecipantRole) && this.config.operatorsStatus.length == 1 ) {
          // Non forzare setAppModes se il disclaimer è pendente:
          // afterLoadInit gestirà il setAppModes dopo l'accettazione del disclaimer
          if (DisclaimerHelper.shouldDisclaimerBeAccepted()) {
            var modes = AppHelper.getModeWhenPrimaryArrives(this.config!.sessionConfig || initialSessionConfig, this.config.AS!.role)
            this.store.dispatch(appActions.setAppModes({ modes }));
          } else {
            console.log(`partecipantAdded - skipping setAppModes: disclaimer pending`);
          }
        }
        //se sono un operatore che condivide la witheboard.. manda segnale al nuovo entrato
        if (this.config.whiteboardSharingEnabled) {
            const val: SharedWhiteboard = {
              enabled: true,
              who: this.config.partecipantId
            }
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.EnableSharedWhiteboard, val, toIds: [partecipantStatus.partecipantId] }));
            const elementsWithoutImage = this.config.whiteboardElements?.filter(z => z.type != ElementType.Image);
            if(elementsWithoutImage?.length) {
                const val = WhiteboardHelper.zipWhiteboardElements(elementsWithoutImage);
                this.store.dispatch(sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.WhiteboardElements, val, toIds: [partecipantStatus.partecipantId] }));
            }
            this.store.dispatch(sessionStatusActions.cmd_dispatch({ 
              cmd: CommandEvent.WhiteboardRelativeTransform, 
              val: this.config.whiteboardTransformData, 
              toIds: appActions.SEND_TO_ALL}))
            
        }




      }
    })
  ), { dispatch: false });

  partecipantRemoved = createEffect(() => this.actions$.pipe(
    ofType(sessionStatusActions.partecipantRemoved),
    withLatestFrom(this.store.select(selectSessionStatusState)),
    tap(([{ partecipantStatus }, state]) => {
    //tap(({ partecipantStatus }) => {
      const who = whoIs(partecipantStatus);
      if (who) {
        NotificationService.showError(`${who} ${I18Service.get('who.Leave')}`);

        //se è uscito un operatore... e non ci sono altri operatori... prima chiudeva le chiamate!! ora tiene in piedi la chiamata
        // if (OPERATOR_ROLES.includes(partecipantStatus.partecipantRole)) {
        //   const anyOperatorOnline = state.sessionStatus.partecipants.some( z=> OPERATOR_ROLES.includes(z.partecipantRole));
        //   if(!anyOperatorOnline){
            
        //     this.closeCallsGoToHome();
        //   }
        // }

        if (this.config.whiteboardIsReceivingShare && partecipantStatus.partecipantId == this.config.whiteboardFromWho) {
          this.whiteboardService.close();
        }

        if (this.config.isReceiver && partecipantStatus.partecipantRole == PartecipantRoles.SecondaryClient) { // è uscito il cittadino, ricaricare l'ultima posizione
          // console.log('********************************* reload last server position')
          this.store.dispatch(sessionActions.lastServerPositionLoad());
        }
      }
      this.sip.endCall(partecipantStatus.partecipantId); //chiude la eventuale chiamata con chi è uscito
    })
  ), { dispatch: false });

  alreadyConnected = createEffect(() => this.actions$.pipe(
    ofType(appActions.readyToStart),
    withLatestFrom(this.store.select(selectPartecipantsStatus)),
    tap(([_, partecipants]) => {// vediamo se ci sono "altri utenti connessi"
      partecipants.forEach(p => {
        if (p.partecipantId != this.config.partecipantId) {
          // const who = whoIs(p);
          // if(!this.config.isCaller) //evita notifiche al cittadino
          // NotificationService.showSuccess(`${who} ${I18Service.get('who.Connected')}`);

          // se il primary è già connesso.. 
          // if (p.partecipantRole == PartecipantRoles.PrimaryClient) {
          //   var mode = AppHelper.getModeWhenPrimaryArrives(this.config!.sessionConfig || initialSessionConfig, this.config.AS!.role)
          //   if (mode) this.store.dispatch(appActions.appModeChange({ mode }));
          // }
        }
      })
    })
  ), { dispatch: false })

  switchCamera$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.switchCamera, appActions.useSelectedCamera),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([action, state]) => {
      const useSelectedCamera = action.type == appActions.useSelectedCamera.type 
      
      if (action.forSnapshot) {
        this.media.switchCameraForSnapshot(useSelectedCamera);
      }
      else {
        this.sip.switchCamera(useSelectedCamera);
      }
    })

  ), { dispatch: false });

  changeMediaDevice$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.changeMediaDevice),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([action, state]) => {

        this.sip.changeMediaDevice();
    })

  ), { dispatch: false });


  zoomTo$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.zoomTo),
    tap( (action) =>{
      this.media.setVideoZoom(action.id);
    })
  ), {dispatch: false})

  fitVideoTo$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.fitVideoTo),
    tap( (action) =>{
      this.media.fitVideoTo(action.id, action.val);
    })
  ), {dispatch: false})

  markMediaAsSeen$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.markMediaAsSeen),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ mediaIdx }, state]) => {

      const m = state.mediaMessages[mediaIdx];
      if (!m) return;
      var markObj: MarkingReadObject = {
        mediaLink: m.content,
        sessionId: state.session.sessionId
      }
      this.eventService.markMediaAsSeen(markObj).subscribe({
        error: (e) => { console.log(`markMediaAsSeen ERROR`, e) }
      })

    })

  ), { dispatch: false })

  chatHistoryLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(sessionActions.chatHistoryLoaded),
    map(() => sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.MarkAsDelivered, val: 'all', toIds: appActions.SEND_TO_ALL }))
  ))

  markMessageAsRead$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.markMessageAsRead),
    map(({ message }) => sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.MarkAllAsRead, val: message, toIds: appActions.SEND_TO_ALL }))

  ))
  recvTextMessage$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.recvTextMessage),
    withLatestFrom(this.store.select(selectAppState)),
    map(([{ from, body, trInfo, contentLanguage, rtpCallId }, state]) => {

      if (state.translationAuto) {// abilitata autotranslate.. e non è stato ancora tradotto nella mia lingua
        if (!(trInfo && trInfo.some(z => z.targetLanguage == this.config.language && z.startingLanguage == contentLanguage))) {
          const message = state.messages.find(z => z.rtpCallId == rtpCallId);
          if (message) {

            this.textTranslationService.translateChatMessage(message, state.translationSelectedLanguage.code, true);
          }
        }
      }



      const m: Message = { idx:0, content: body, from, timestamp: new Date() }
      return sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.MarkAsDelivered, val: m, toIds: [from] })
    })
  ))
  recvMediaMessage$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.recvMediaMessage),
    map(({ message }) => {
      return sessionStatusActions.cmd_dispatch({ cmd: CommandEvent.MarkAsDelivered, val: message, toIds: [message.from] })
    })
  ))


  myPartecipantStatusChange = createEffect(() => this.actions$.pipe(
    ofType(sessionStatusActions.partecipantStatusChange),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ partecipantStatus }, appState]) => {
      if (appState.partecipantId == partecipantStatus.partecipantId) {
        // #95328: cattura stato PRIMA di sovrascrivere
        const serverVideoEnabled = partecipantStatus.videoEnabled;
        const localVideoWasOff = !appState.videoEnabled;
        const notYetInCall = !this.config.iamOnCall;

        this.store.dispatch(appActions.myAppStateChange({partecipantStatus}));

        // #95328: se il server conferma videoEnabled ma localmente era off e non siamo in call,
        // handleCalls() potrebbe essere fallito per race condition setAppModes vs myAppStateChange.
        // setTimeout(0) è necessario perché NgRx accoda la dispatch di myAppStateChange quando siamo
        // già dentro la pipeline di partecipantStatusChange: il reducer gira DOPO il tap(), quindi
        // config.AS.videoEnabled è ancora false se chiamiamo handleCalls() in modo sincrono.
        if (serverVideoEnabled && localVideoWasOff && notYetInCall) {
          console.log(`#95328 retry handleCalls | server.videoEnabled:${serverVideoEnabled}, local.videoEnabled:${appState.videoEnabled}, iamOnCall:${this.config.iamOnCall}, server.phoneStatus:${partecipantStatus.phoneStatus}, server.appModes:[${partecipantStatus.appModes}]`);
          setTimeout(() => this.sip.handleCalls(), 0);
        }
      }
    })
  ), { dispatch: false })

  // Quando un operatore completa la registrazione SIP (transizione !isRegistered → isRegistered),
  // triggerare handleCalls per instaurare la chiamata.
  // L'action operatorSipRegistered è emessa da signalrhub.effects.ts confrontando lo stato precedente.
  operatorSipRegistered$ = createEffect(() => this.actions$.pipe(
    ofType(sessionStatusActions.operatorSipRegistered),
    tap(({ partecipantStatus }) => {
      // if (!this.config.isReceiver) {
        console.log(`operatorSipRegistered$ - operator ${partecipantStatus.partecipantId} registered, triggering handleCalls`);
        this.sip.handleCalls();
      // }
    })
  ), { dispatch: false });


  private handleStaleAppModes(state: AppState) {
    NotificationService.popupClose();
    setTimeout(() => {
      this.store.select(myPartecipantStatus).pipe(
        take(1)
      ).subscribe(myStatus => {
        const appModes = myStatus?.appModes || [];

        // Se il disclaimer è richiesto e non ancora accettato, appModes è intenzionalmente vuoto:
        // afterLoadInit aspetta disclaimerAccepted prima di chiamare goToInitialAppMode().
        // Non forzare recovery in questo caso, altrimenti bypassa il guard del disclaimer.
        const disclaimerRequired = this.config.agencySettings?.showDisclaimer && (this.config.isCaller || this.config.isGuest);
        const disclaimerPending = disclaimerRequired && !myStatus?.disclaimerAccepted;

        if (appModes.length === 0 && !disclaimerPending) {
          // Log SOLO verso backend per tracciare il problema
          FELoggerService.warn('appModes empty after partecipantAdded - forcing recovery', LogSource.Default, {
            partecipantId: this.config.partecipantId,
            role: myStatus?.partecipantRole || state.role,
            operatorsOnline: this.config.operatorsStatus.length
          });

          // Calcola stato corretto e dispatch
          const anyOpOnline = this.config.operatorsStatus.length > 0;
          const myRole = myStatus?.partecipantRole || state.role;
          const sessionConfig = this.config.sessionConfig || initialSessionConfig;
          const modes = AppHelper.getInitialAppMode(sessionConfig, myRole, anyOpOnline);

          // Dispatch setAppModes → l'effect appModeChange$ notificherà automaticamente il backend
          this.store.dispatch(appActions.setAppModes({ modes }));
        } else if (disclaimerPending) {
          console.log(`handleStaleAppModes - skipping recovery: disclaimer pending`);
        }
      });
    }, 5000);
  }

  private closeCallsGoToHome() {
    console.log(`Nessun operatore online, torno alla pagina iniziale e chiudo chiamate`);

    // IMPORTANTE: Prima chiudiamo le chiamate, POI cambiamo mode
    // Questo previene che handleCalls() venga chiamato mentre le sessioni sono ancora attive
    this.sip.endAllCalls();

    // Aspettiamo che onCallHangup aggiorni il phoneStatus prima di cambiare mode
    setTimeout(() => {
      const anyOpOnline = this.config.operatorsStatus.length > 0;

      const modes = AppHelper.getHome(this.config.sessionConfig!, AppConfigService.role, anyOpOnline);

      console.log(`Cambio mode a: ${modes}`);
      this.store.dispatch(appActions.setAppModes({ modes }));
      console.log(`closeCallsGoToInitialPage - goToInitialAppMode mode:'${modes}' for ${AppConfigService.role} and anyOpOnline: ${anyOpOnline}`);
    }, 500);
  }

  private openWhiteboardImage(image: CanvasData | undefined) {
    setTimeout(() => {
      
      if (image) {
        this.whiteboardService.open(image);
        const sub = this.whiteboardService.onClose$.subscribe(saved => {
          // if(saved) {
          this.config.whiteboardReset();
          // }
          sub.unsubscribe();
        });
      }
    }, 200);
  }
  
  
  // Effect per gestire l'uscita da suspended mode
  // Quando suspended diventa vuoto (false), forza riconnessione SIP se necessario
  onExitSuspendedMode$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.setSuspended),
    withLatestFrom(this.store.select(selectAppState)),
    tap(([{ message }, appState]) => {
      // Se stiamo USCENDO dal suspended mode (message vuoto)
      // e SIP non è registrato, forza riconnessione
      if (!message && !appState.wssSipRegistered) {
        console.log('Exiting suspended mode - forcing SIP reconnection');
        this.sip.init(); // Forza riconnessione SIP
      }
    })
  ), { dispatch: false })


  // ========================================
  // AUTO-RESET LOADING STATES
  // ========================================

  private suspendedTimeoutId: any = null;
  autoResetSuspended$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.setSuspended),
    tap(({ message, autoResetTimeout }) => {
      if (this.suspendedTimeoutId) {
        clearTimeout(this.suspendedTimeoutId);
        this.suspendedTimeoutId = null;
      }

      if (message && message.trim() !== '') {
        const timeout = autoResetTimeout ?? Constants.AUTORESET_TIMEOUT_MS; // Default 60s
        console.log(`Auto-reset suspended scheduled in ${timeout}ms`);

        this.suspendedTimeoutId = setTimeout(() => {
          console.warn(`Auto-reset suspended triggered after ${timeout}ms`);
          this.store.dispatch(appActions.setSuspended({ message: '' }));
          this.suspendedTimeoutId = null;
        }, timeout);
      }
    })
  ), { dispatch: false });

  private loadingTimeoutId: any = null;
  autoResetLoadingMessage$ = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadingMessage),
    tap(({ message, autoResetTimeout }) => {
      if (this.loadingTimeoutId) {
        clearTimeout(this.loadingTimeoutId);
        this.loadingTimeoutId = null;
      }

      if (message && message.trim() !== '') {
        const timeout = autoResetTimeout ?? Constants.AUTORESET_TIMEOUT_MS; // Default 60s
        console.log(`Auto-reset loadingMessage scheduled in ${timeout}ms`);

        this.loadingTimeoutId = setTimeout(() => {
          console.warn(`Auto-reset loadingMessage triggered after ${timeout}ms`);
          this.store.dispatch(appActions.loadingMessage({ message: '' }));
          this.loadingTimeoutId = null;
        }, timeout);
      }
    })
  ), { dispatch: false });
}


function whoIs(partecipantStatus: PartecipantStatus): string {

  return I18Service.roleAndAlias(partecipantStatus);
}
